import { Select, Button, cn } from "@tellescope/design-system";
import {
  TellescopeModelFromName,
  useAuth,
  usePermissionQuery,
  useTellescopeMutation,
} from "@tellescope/tanstack-query";
import { useCallback, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { DestructiveConfirmDialog, Input } from "@tellescope/ui";
import { isFetchableViewId } from "./constants";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useWritableViewId } from "../../hooks/use-writable-view-id";

export interface ViewSelectItemProps {
  view: TellescopeModelFromName<"custom_dashboards">;
  isLastView: boolean;
  isDefault?: boolean;
  onDeleted?: (viewId: string) => void;
  onEdit?: (viewId: string) => void;
}

export const ViewSelectItem = ({ view, isLastView, isDefault, onDeleted, onEdit }: ViewSelectItemProps) => {
  const { t } = useTranslation("contacts");
  const { currentUser, isAdmin } = useAuth();
  const isPlaceholderView = !isFetchableViewId(view.id);
  const isOwnView = isPlaceholderView || view.creator === currentUser.id;
  const [newName, setNewName] = useState<string>();

  const {
    update: { access: canEdit },
    delete: { access: canDelete },
    create: { access: canCreate },
  } = usePermissionQuery({
    model: "custom_dashboards",
    record: view,
    actions: ["update", "delete", "create"],
  });
  const showDelete = canDelete && !isLastView && (isAdmin || isOwnView);
  const showEdit = (isPlaceholderView ? canCreate : canEdit) && (isAdmin || isOwnView);

  const resolveWritableViewId = useWritableViewId({ viewId: view.id });
  const editViewMutation = useTellescopeMutation({
    model: "custom_dashboards",
    method: "updateOne",
  });
  const deleteViewMutation = useTellescopeMutation({
    model: "custom_dashboards",
    method: "deleteOne",
  });

  const handleEditClick = useCallback(async () => {
    if (!isAdmin) {
      setNewName(view.title);
      return;
    }

    const writableView = await resolveWritableViewId().catch(() => {
      toast.error(t("Something went wrong... Please try again"));
      return undefined;
    });
    if (!writableView) return;

    onEdit?.(writableView.id);
  }, [isAdmin, onEdit, resolveWritableViewId, t, view.title]);

  const handleSetNewName = useCallback(
    async (updatedName: string) => {
      const writableView = await resolveWritableViewId().catch(() => {
        toast.error(t("Something went wrong... Please try again"));
        return undefined;
      });
      if (!writableView) return;

      return editViewMutation.mutateAsync(
        {
          id: writableView.id,
          title: updatedName,
        },
        {
          onSuccess: () => {
            setNewName(undefined);
          },
          onError: () => {
            toast.error(t("Something went wrong... Please try again"));
          },
        }
      );
    },
    [editViewMutation, resolveWritableViewId, t]
  );

  return (
    <>
      <span className={cn("flex items-center gap-1 group", newName !== undefined && "hidden")}>
        <Select.Item value={view.id} className={cn("grow justify-start")}>
          <span className="truncate">{view.title}</span>
          {isDefault && <span className="ml-1 shrink-0 text-muted-foreground">{t("(default)")}</span>}
        </Select.Item>
        {showEdit && (
          <Button
            iconOnly
            variant="ghost"
            size="sm"
            color="muted"
            onClick={handleEditClick}
            className="invisible group-hover:visible"
          >
            <Pencil />
          </Button>
        )}
        {showDelete && (
          <DestructiveConfirmDialog
            onConfirm={() =>
              deleteViewMutation.mutateAsync(view.id, {
                onSuccess: () => onDeleted?.(view.id),
                onError: () => {
                  toast.error(t("Something went wrong... Please try again"));
                },
              })
            }
            description={t("This will permanently delete the view for everyone. This action cannot be undone.")}
          >
            <Button iconOnly variant="ghost" size="sm" color="destructive" className="invisible group-hover:visible">
              <Trash2 />
            </Button>
          </DestructiveConfirmDialog>
        )}
      </span>
      {newName !== undefined && (
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter") {
              handleSetNewName(newName);
            } else if (e.key === "Escape") {
              setNewName(undefined);
            }
          }}
          className="grow border-none"
          autoFocus
          onBlur={() => setNewName(undefined)}
        />
      )}
    </>
  );
};
